/**
 * Session boundary for the Open Sky console (transitional).
 *
 * Real mode wraps the transplanted Open Sky shell in the LIVE session gate. An unauthenticated
 * visitor gets the real Login (posts /api/auth/login, sets the cookie); a signed-in one gets the
 * console. A non-auth failure (API unreachable) says so and offers retry — it never blanks, and
 * it says the part that matters at 2 AM: the phone line does not depend on this console.
 *
 * The DemoProvider still sits INSIDE the gate during the transplant, so screens not yet wired to
 * the typed API keep rendering on the in-memory adapter. As each screen moves to real hooks its
 * demo reads fall away; when the last screen is wired, DemoProvider and this note go with it.
 *
 * Unlike the old console's RequireSession, this one does NOT mount AppShell — the Open Sky App is
 * its own shell. The go-live dialog (old AppShell's demo band) is not yet re-hosted here; it is
 * re-homed onto Open Sky's demo strip when Configure/Settings is wired.
 */
import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import type { ReactElement, ReactNode } from 'react';

import { setUnauthorizedHandler } from '../lib/api.ts';
import { useMe } from '../lib/useApi.ts';
import { ErrorState } from '../components/index.ts';
import Login from '../screens/Login.tsx';
import PricingPage from '../pricing/PricingPage.tsx';
import Welcome from '../pricing/Welcome.tsx';
import { DemoProvider } from './lib/demo.tsx';
import { App } from './App.tsx';

function RequireSession({ children }: { children: ReactNode }): ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useMe();

  // A mid-session 401 (cookie expired under the operator) bounces to login instead of leaving a
  // dead console open. Cleared on unmount so a stale closure never fires against a later route.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      const next = encodeURIComponent(location.pathname + location.search);
      navigate(`/login?next=${next}`, { replace: true });
    });
    return () => setUnauthorizedHandler(null);
  }, [navigate, location.pathname, location.search]);

  if (isLoading) {
    return (
      <div className="nd-boot">
        <span className="nd-brand-mark" aria-hidden="true">
          N
        </span>
        <span>Opening NightDesk…</span>
      </div>
    );
  }

  if (isError && error?.status === 401) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  if (isError || !data) {
    return (
      <div className="nd-boot">
        <ErrorState
          error={error}
          action="reach NightDesk"
          onRetry={() => {
            void refetch();
          }}
        />
      </div>
    );
  }

  return (
    <DemoProvider>
      {children}
    </DemoProvider>
  );
}

export function Root(): ReactElement {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* Public, un-gated: a prospect who has never signed in can reach these. Kept out of
          RequireSession so the pricing page never bounces a logged-out visitor to /login. */}
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/welcome" element={<Welcome />} />
      <Route
        path="*"
        element={
          <RequireSession>
            <App />
          </RequireSession>
        }
      />
    </Routes>
  );
}

export default Root;
